import { useEffect, useState, useCallback } from "react";
import { useApi } from "../useApi";
import { useProject } from "../projectContext";
import type { Project } from "../types";

interface TokenValidationResult {
  valid: boolean;
  error?: string;
  expiresAt?: string;
  expiresInSeconds?: number;
  toolCount?: number;
}

interface ValidationState {
  atlassian?: TokenValidationResult;
  sharepoint?: TokenValidationResult;
  loading: boolean;
}

function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) return "expired";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export default function AccessTokensPage() {
  const { project: summary } = useProject();
  const { call } = useApi();
  const [project, setProject] = useState<Project | undefined>();
  const [atlassian, setAtlassian] = useState("");
  const [sharepoint, setSharepoint] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [validation, setValidation] = useState<ValidationState>({ loading: false });

  const validateTokens = useCallback(
    async (overrides?: { atlassianAccessToken?: string; sharepointAccessToken?: string }) => {
      setValidation((v) => ({ ...v, loading: true }));
      try {
        const result = await call<{ atlassian: TokenValidationResult; sharepoint: TokenValidationResult }>(
          `/api/projects/${summary.projectId}/tokens/validate`,
          {
            method: "POST",
            body: JSON.stringify(overrides ?? {}),
          }
        );
        setValidation({ atlassian: result.atlassian, sharepoint: result.sharepoint, loading: false });
      } catch {
        setValidation((v) => ({ ...v, loading: false }));
      }
    },
    [call, summary.projectId]
  );

  useEffect(() => {
    call<Project>(`/api/projects/${summary.projectId}`)
      .then((p) => {
        setProject(p);
        // Validate stored tokens on page load
        validateTokens();
      })
      .catch((e: Error) => setError(e.message));
  }, [call, summary.projectId, validateTokens]);

  const save = async () => {
    setSaving(true);
    setMessage(undefined);
    setError(undefined);
    try {
      await call(`/api/projects/${summary.projectId}/tokens`, {
        method: "PUT",
        body: JSON.stringify({
          atlassianAccessToken: atlassian || undefined,
          sharepointAccessToken: sharepoint || undefined,
        }),
      });
      setMessage("Access tokens updated.");
      // Validate the newly saved tokens
      await validateTokens({
        atlassianAccessToken: atlassian || undefined,
        sharepointAccessToken: sharepoint || undefined,
      });
      setAtlassian("");
      setSharepoint("");
      const refreshed = await call<Project>(`/api/projects/${summary.projectId}`);
      setProject(refreshed);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!project) return <p>Loading…</p>;

  return (
    <>
      <div className="page-header">
        <h1>Access Tokens</h1>
        <p>
          Both tokens are time-limited and must be obtained manually. Leave a field blank to keep
          its current value.
        </p>
      </div>

      <div className="card">
        <h2>Atlassian access token</h2>
        <div className="callout">
          <strong>How to obtain:</strong>
          <ol>
            <li>
              Run <code>npx @modelcontextprotocol/inspector https://mcp.atlassian.com/v1/mcp --transport http</code>
            </li>
            <li>Check that Transport Type is "Streamable HTTP" and URL is <code>https://mcp.atlassian.com/v1/mcp</code></li>
            <li>Click "Open Auth Settings"</li>
            <li>Click "Quick OAuth Flow"</li>
            <li>Approve access to Jira and Confluence for the organisation's Atlassian site</li>
            <li>Scroll down and copy the Access Token</li>
          </ol>
        </div>
        <TokenStatus result={validation.atlassian} loading={validation.loading} type="atlassian" />
        <div className="field">
          <label>Current token</label>
          <input value={project.atlassianAccessToken ? "••••••••" : "(none)"} disabled />
        </div>
        <div className="field">
          <label>New token</label>
          <input
            type="password"
            value={atlassian}
            onChange={(e) => setAtlassian(e.target.value)}
            placeholder="Paste new token to replace"
          />
        </div>
      </div>

      <div className="card">
        <h2>SharePoint (Graph API) access token</h2>
        <div className="callout">
          <strong>How to obtain:</strong>
          <ol>
            <li>Open browser</li>
            <li>Open Developer Tools</li>
            <li>Open Network tab</li>
            <li>Filter only Fetch/XHR requests</li>
            <li>Filter <code>graph.microsoft.com</code></li>
            <li>Navigate to https://vaisalaworks.sharepoint.com/sites/vintra-maritime/SitePages/Elements.aspx</li>
            <li>Select the request that begins with "people"</li>
            <li>Copy the <code>Authorization</code> header value (excluding the "Bearer" prefix) from one of the requests</li>
          </ol>
        </div>
        <TokenStatus result={validation.sharepoint} loading={validation.loading} type="sharepoint" />
        <div className="field">
          <label>Current token</label>
          <input value={project.sharepointAccessToken ? "••••••••" : "(none)"} disabled />
        </div>
        <div className="field">
          <label>New token</label>
          <input
            type="password"
            value={sharepoint}
            onChange={(e) => setSharepoint(e.target.value)}
            placeholder="Paste new token to replace"
          />
        </div>
      </div>

      <div className="card actions">
        {error && <span className="error" style={{ marginRight: 12 }}>{error}</span>}
        {message && <span style={{ marginRight: 12 }}>{message}</span>}
        <button disabled={saving || (!atlassian && !sharepoint)} onClick={save}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </>
  );
}

function TokenStatus({
  result,
  loading,
  type,
}: {
  result?: TokenValidationResult;
  loading: boolean;
  type: "atlassian" | "sharepoint";
}) {
  if (loading) {
    return <div className="token-status token-status--checking">Checking token…</div>;
  }
  if (!result) return null;

  if (result.valid) {
    if (type === "sharepoint" && result.expiresInSeconds != null && result.expiresInSeconds < 900) {
      return (
        <div className="token-status token-status--warning">
          <strong>⚠ Expiring soon</strong> — expires in {formatTimeRemaining(result.expiresInSeconds)}.
          Please obtain a new token using the instructions above.
        </div>
      );
    }
    const detail =
      type === "atlassian"
        ? `Valid — ${result.toolCount} tools available`
        : `Valid — expires in ${formatTimeRemaining(result.expiresInSeconds ?? 0)}`;
    return <div className="token-status token-status--valid">{detail}</div>;
  }

  return (
    <div className="token-status token-status--invalid">
      <strong>⚠ Token invalid</strong> — {result.error}.{" "}
      Please obtain a new token using the instructions above.
    </div>
  );
}
